import React from 'react';
import { Link } from 'react-router-dom';
import { colors } from '../theme';
import UidMap from './UidMap';
import { formatDisplayDate, formatLocal, formatCoordinate, isValidCoordinate } from './utils';

export default function UidCard({ tenant, uid, customName, scansCount, firstSeen, lastSeen, lastLocation }) {
  const displayName = customName?.trim() || uid;
  const ageLabel = firstSeen ? formatDisplayDate(firstSeen) : 'Unknown';
  const lastSeenLabel = lastSeen ? formatLocal(lastSeen) : 'Unknown';

  const hasLocation = isValidCoordinate(lastLocation?.lat) && isValidCoordinate(lastLocation?.lon);
  const coordinates = hasLocation ? [{ lat: lastLocation.lat, lon: lastLocation.lon }] : [];

  return (
    <article style={cardStyle}>
      <header style={cardHeaderStyle}>
        <div>
          <h2 style={titleStyle}>{displayName}</h2>
          {customName ? <p style={subtitleStyle}>UID: {uid}</p> : <p style={subtitleStyle}>&nbsp;</p>}
        </div>
        <Link to={`/farm/${tenant}/uid/${encodeURIComponent(uid)}`} style={linkStyle}>
          Open
        </Link>
      </header>
      <section style={metricsRowStyle}>
        <div style={metricStyle}>
          <span style={metricLabelStyle}>Age</span>
          <span style={metricValueStyle}>{ageLabel}</span>
        </div>
        <div style={metricStyle}>
          <span style={metricLabelStyle}>Last seen</span>
          <span style={metricValueStyle} title={lastSeen || ''}>{lastSeenLabel}</span>
        </div>
        <div style={metricStyle}>
          <span style={metricLabelStyle}>Scans</span>
          <span style={metricValueStyle}>{scansCount?.toLocaleString?.() ?? scansCount ?? 0}</span>
        </div>
        <div style={metricStyle}>
          <span style={metricLabelStyle}>Location</span>
          <span style={metricValueStyle}>
            {hasLocation
              ? `${formatCoordinate(lastLocation.lat)}, ${formatCoordinate(lastLocation.lon)}`
              : 'Not available'}
          </span>
        </div>
      </section>
      <div style={{ marginTop: '1rem' }}>
        <UidMap
          points={coordinates}
          height={160}
          showTrail={false}
          autoFit={false}
          maxZoom={12}
          style={{ borderRadius: '14px' }}
        />
      </div>
    </article>
  );
}

const cardStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '1.1rem',
  borderRadius: '18px',
  backgroundColor: 'white',
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
  border: '1px solid rgba(71, 187, 137, 0.1)',
  minWidth: 0,
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '0.75rem',
};

const titleStyle = {
  margin: 0,
  fontSize: '1.15rem',
  color: colors.text,
};

const subtitleStyle = {
  margin: '0.2rem 0 0',
  fontSize: '0.85rem',
  color: colors.muted,
};

const linkStyle = {
  alignSelf: 'flex-start',
  padding: '0.45rem 0.95rem',
  borderRadius: '999px',
  backgroundColor: colors.primary,
  color: colors.background,
  fontWeight: 600,
  textDecoration: 'none',
};

const metricsRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: '0.75rem',
};

const metricStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  padding: '0.6rem 0.75rem',
  borderRadius: '12px',
  backgroundColor: 'rgba(70,187,137,0.08)',
};

const metricLabelStyle = {
  fontSize: '0.75rem',
  color: colors.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const metricValueStyle = {
  fontSize: '1rem',
  fontWeight: 600,
  color: colors.text,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
